import React, { useState } from 'react';
import styled from 'styled-components';
import { Menu, MenuItem } from '@material-ui/core';
import { ITranslationProjectTranslationItem } from 'types/translationProject';

type TranslationActionItem = Pick<ITranslationProjectTranslationItem, 'id' | 'async_task_status' | 'is_validated'>;

interface ITranslationProjectPreTranslateActionsProps {
  items: TranslationActionItem[];
  projectId: number;
  onActionTrigger: (method: 'memory' | 'ai' | 'copy' | 'delete') => Promise<void>;
  disabled?: boolean;
  awaitingMemoryCount?: number;
}

const TranslationProjectPreTranslateActions: React.FC<ITranslationProjectPreTranslateActionsProps> = ({
  items,
  projectId,
  onActionTrigger,
  disabled = false,
  awaitingMemoryCount = 0,
}) => {
  const [bulkMenuAnchor, setBulkMenuAnchor] = useState<null | HTMLElement>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Exclude validated items from actions
  const nonValidatedItems = items.filter((item) => !item.is_validated);
  const totalCount = nonValidatedItems.length;

  const handleBulkMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setBulkMenuAnchor(event.currentTarget);
  };

  const handleBulkMenuClose = () => {
    setBulkMenuAnchor(null);
  };

  const handleAction = async (method: 'memory' | 'ai' | 'copy' | 'delete') => {
    handleBulkMenuClose();
    if (isProcessing) return;

    setIsProcessing(true);
    try {
      await onActionTrigger(method);
    } catch (error) {
      console.error(`Error during ${method} action:`, error);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <>
      <BulkActionsButton onClick={handleBulkMenuClick} disabled={isProcessing || disabled || totalCount === 0}>
        {isProcessing ? 'Processing...' : 'Actions'}
      </BulkActionsButton>
      <Menu anchorEl={bulkMenuAnchor} open={Boolean(bulkMenuAnchor)} onClose={handleBulkMenuClose}>
        <MenuItem onClick={() => handleAction('memory')} disabled={totalCount === 0}>
          Memory Translation ({totalCount})
          {awaitingMemoryCount > 0 && <MemoryHint> — {awaitingMemoryCount} awaiting</MemoryHint>}
        </MenuItem>
        <MenuItem onClick={() => handleAction('ai')} disabled={totalCount === 0}>
          AI Translation ({totalCount})
        </MenuItem>
        <MenuItem onClick={() => handleAction('copy')} disabled={totalCount === 0}>
          Copy Source to Target ({totalCount})
        </MenuItem>
        <MenuItem onClick={() => handleAction('delete')} disabled={totalCount === 0}>
          Delete All Translations ({totalCount})
        </MenuItem>
      </Menu>
    </>
  );
};

export default TranslationProjectPreTranslateActions;

const MemoryHint = styled.span`
  color: #78909C;
  font-size: 12px;
  font-weight: 600;
`;

const BulkActionsButton = styled.button`
  padding: 8px 16px;
  background-color: #4caf50;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.3s;

  &:hover:not(:disabled) {
    background-color: #45a049;
  }

  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`;
